import { forwardRef, type FormEvent, type ReactNode } from 'react';
import { IconClose, IconSearch } from '@/shared/ui/icons';
import styles from './SearchField.module.less';

interface SearchFieldProps {
  /** Подсказка в поле ввода, она же подпись в режиме кнопки */
  placeholder: string;
  /**
   * Текущий текст. Есть onChange — поле принимает ввод; нет — это кнопка,
   * которая уводит на экран с настоящим поиском (Главная, корзина аптеки)
   */
  value?: string;
  onChange?: (value: string) => void;
  onSubmit?: () => void;
  onClick?: () => void;
  disabled?: boolean;
  /** Ставить курсор в поле сразу при появлении экрана */
  autoFocus?: boolean;
  /** Правый слот: кнопка «Найти» на Главной */
  action?: ReactNode;
}

/**
 * Поле поиска, одно на все экраны. Лупа слева и работает кнопкой отправки,
 * справа — крестик очистки или переданный слот.
 */
export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  function SearchField(
    {
      placeholder,
      value = '',
      onChange,
      onSubmit,
      onClick,
      disabled = false,
      autoFocus = false,
      action,
    },
    ref
  ) {
    if (!onChange) {
      return (
        <button
          type="button"
          className={styles.field}
          onClick={onClick}
          disabled={disabled}
          aria-label={placeholder}
        >
          <IconSearch className={styles.icon} aria-hidden="true" />
          <span className={styles.placeholder}>{placeholder}</span>
          {action}
        </button>
      );
    }

    const handleSubmit = (event: FormEvent) => {
      event.preventDefault();
      if (disabled) return;
      onSubmit?.();
    };

    return (
      <form className={styles.field} onSubmit={handleSubmit} role="search">
        <button
          type="submit"
          className={styles.iconBtn}
          aria-label="Найти"
          disabled={disabled}
        >
          <IconSearch className={styles.icon} />
        </button>

        <input
          ref={ref}
          className={styles.input}
          type="search"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          value={value}
          placeholder={placeholder}
          aria-label={placeholder}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />

        {value && (
          <button
            type="button"
            className={styles.iconBtn}
            aria-label="Очистить"
            onClick={() => onChange('')}
          >
            <IconClose className={styles.icon} />
          </button>
        )}

        {action}
      </form>
    );
  }
);
